import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { TrendingUp, Users, Star, ArrowRight, CheckCircle, Clapperboard, BookOpen, Briefcase, FlaskConical, Dumbbell, Guitar, UserCircle2, Clock, Film } from 'lucide-react'
import { Link } from 'react-router-dom'
import { getLeaderboard, getCelebrities, getStats, getRecentlyAdded } from '@/lib/api'
import type { CelebrityCategory } from '@/types'
import SEO from '@/components/SEO'
import ThreeBackground from '@/components/ThreeBackground'
import { useRecentlyViewed } from '@/hooks/useRecentlyViewed'
import { subscribeToNewsletter } from '@/lib/api'
import { sanitizeEmail } from '@/lib/sanitize'

const CATEGORIES: { value: CelebrityCategory; label: string; icon: React.ReactNode; color: string }[] = [
  { value: 'actor', label: 'Actors', icon: <Clapperboard className="h-6 w-6" />, color: 'bg-rose-100 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400' },
  { value: 'director', label: 'Directors', icon: <Clapperboard className="h-6 w-6" />, color: 'bg-violet-100 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400' },
  { value: 'entrepreneur', label: 'Entrepreneurs', icon: <Briefcase className="h-6 w-6" />, color: 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400' },
  { value: 'author', label: 'Authors', icon: <BookOpen className="h-6 w-6" />, color: 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400' },
  { value: 'musician', label: 'Musicians', icon: <Guitar className="h-6 w-6" />, color: 'bg-pink-100 dark:bg-pink-900/30 text-pink-600 dark:text-pink-400' },
  { value: 'scientist', label: 'Scientists', icon: <FlaskConical className="h-6 w-6" />, color: 'bg-sky-100 dark:bg-sky-900/30 text-sky-600 dark:text-sky-400' },
  { value: 'athlete', label: 'Athletes', icon: <Dumbbell className="h-6 w-6" />, color: 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400' },
  { value: 'politician', label: 'Public Figures', icon: <UserCircle2 className="h-6 w-6" />, color: 'bg-teal-100 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400' },
]

function MediaCard({ item }: { item: any }) {
  return (
    <Link to={`/media/${item.slug}`} className="group block">
      <div className="aspect-[2/3] rounded-xl overflow-hidden bg-gray-200 dark:bg-slate-700 relative shadow-sm group-hover:shadow-xl transition-all duration-300">
        {item.posterUrl ? (
          <img
            src={item.posterUrl}
            alt={item.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400 dark:text-slate-500 text-xs">
            No Image
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div className="absolute bottom-0 left-0 right-0 p-2">
            <p className="text-white text-xs font-medium truncate">{item.title}</p>
            <p className="text-primary-300 text-xs">{item.celebrityCount} picks</p>
          </div>
        </div>
      </div>
      <div className="mt-2 px-0.5">
        <h3 className="font-semibold text-gray-900 dark:text-slate-100 truncate text-xs sm:text-sm leading-tight">{item.title}</h3>
        <p className="text-xs text-gray-500 dark:text-slate-500 mt-0.5">{item.year}</p>
      </div>
    </Link>
  )
}

function MediaCardSkeleton() {
  return (
    <div className="animate-pulse">
      <div className="aspect-[2/3] rounded-xl bg-gray-200 dark:bg-slate-700" />
      <div className="mt-2 h-3 bg-gray-200 dark:bg-slate-700 rounded" />
      <div className="mt-1 h-2.5 w-2/3 bg-gray-100 dark:bg-slate-800 rounded" />
    </div>
  )
}

function CelebrityCardSkeleton() {
  return (
    <div className="text-center animate-pulse">
      <div className="aspect-square rounded-full bg-gray-200 dark:bg-slate-700 mb-2 sm:mb-3 mx-auto max-w-[72px] sm:max-w-[96px] lg:max-w-[112px]" />
      <div className="h-3 bg-gray-200 dark:bg-slate-700 rounded mx-2" />
      <div className="h-2.5 bg-gray-100 dark:bg-slate-800 rounded mx-6 mt-1" />
    </div>
  )
}

export default function HomePage() {
  const { data: movies, isLoading: isLoadingMovies } = useQuery({
    queryKey: ['leaderboard', 6, 'movie'],
    queryFn: () => getLeaderboard(6, 'movie'),
  })

  const { data: shows, isLoading: isLoadingShows } = useQuery({
    queryKey: ['leaderboard', 6, 'tv_show'],
    queryFn: () => getLeaderboard(6, 'tv_show'),
  })

  const { data: celebrities, isLoading: isLoadingCelebrities } = useQuery({
    queryKey: ['celebrities', 1, 6],
    queryFn: () => getCelebrities(1, 6),
  })

  const { data: stats } = useQuery({
    queryKey: ['stats'],
    queryFn: getStats,
    staleTime: 1000 * 60 * 60,
  })

  const { data: recentlyAdded } = useQuery({
    queryKey: ['recently-added'],
    queryFn: getRecentlyAdded,
    staleTime: 1000 * 60 * 60,
  })

  const recentlyViewed = useRecentlyViewed()
  const [newsletterEmail, setNewsletterEmail] = useState('')
  const [newsletterMsg, setNewsletterMsg] = useState('')

  async function handleNewsletterSubmit(e: React.FormEvent) {
    e.preventDefault()
    const email = sanitizeEmail(newsletterEmail)
    const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
    if (!valid) {
      setNewsletterMsg('Enter a valid email address.')
      return
    }
    try {
      const res = await subscribeToNewsletter(email)
      if (!res.ok) throw new Error('subscribe failed')
      setNewsletterEmail('')
      setNewsletterMsg('Subscribed successfully.')
    } catch {
      setNewsletterMsg('Could not subscribe right now. Please try again.')
    }
  }

  const celebCount = stats?.celebrities ? `${stats.celebrities}+` : '100+'
  const recCount = stats?.recommendations ? `${stats.recommendations}+` : '500+'

  return (
    <div>
      <SEO canonical="/" />

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-primary-50 via-primary-100/60 to-white dark:bg-slate-950 dark:from-primary-950 dark:via-slate-950 dark:to-slate-900">
        {/* 3D animated background — sits behind the blobs and content */}
        <ThreeBackground className="absolute inset-0 z-0" />
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary-200/50 dark:bg-primary-600/20 rounded-full blur-3xl pointer-events-none" aria-hidden="true" />
        <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-primary-100/70 dark:bg-accent-500/10 rounded-full blur-3xl pointer-events-none" aria-hidden="true" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14 text-center">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-3 sm:mb-4 leading-tight tracking-tight text-gray-900 dark:text-white">
            Discover What the
            <br className="hidden sm:block" />
            <span className="bg-gradient-to-r from-primary-600 via-primary-500 to-primary-700 dark:from-primary-400 dark:via-primary-300 dark:to-accent-400 bg-clip-text text-transparent">
              {' '}World's Best Minds{' '}
            </span>
            Watch
          </h1>

          <p className="text-sm sm:text-base md:text-lg text-gray-600 dark:text-slate-300 max-w-2xl mx-auto mb-6 sm:mb-8 leading-relaxed px-2 sm:px-0">
            Movie and TV recommendations from celebrities, directors,
            entrepreneurs, and public intellectuals.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/browse"
              className="px-6 py-2.5 bg-primary-600 hover:bg-primary-500 text-white rounded-xl font-semibold transition-all duration-200 shadow-lg shadow-primary-200 dark:shadow-primary-900/40 hover:shadow-primary-300 dark:hover:shadow-primary-800/40 hover:-translate-y-0.5 text-sm sm:text-base"
            >
              Browse Recommendations
            </Link>
            <Link
              to="/leaderboard"
              className="px-6 py-2.5 bg-white/80 hover:bg-white dark:bg-white/10 dark:hover:bg-white/20 text-gray-800 dark:text-white border border-gray-200 dark:border-white/20 rounded-xl font-semibold transition-all duration-200 hover:-translate-y-0.5 text-sm sm:text-base shadow-sm dark:shadow-none"
            >
              See Top Picks
            </Link>
          </div>

          <div className="flex flex-wrap justify-center gap-5 sm:gap-8 mt-8 sm:mt-10 text-xs sm:text-sm text-gray-500 dark:text-slate-400">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-3.5 w-3.5 text-primary-500 dark:text-primary-400" aria-hidden="true" />
              <span>{celebCount} Celebrities</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-3.5 w-3.5 text-primary-500 dark:text-primary-400" aria-hidden="true" />
              <span>{recCount} Verified Picks</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-3.5 w-3.5 text-primary-500 dark:text-primary-400" aria-hidden="true" />
              <span>Every Source Tracked</span>
            </div>
          </div>
        </div>
      </section>

      {/* Most Recommended Movies */}
      <section className="py-12 sm:py-14 lg:py-16 bg-white dark:bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-6 sm:mb-8">
            <div>
              <p className="text-xs sm:text-sm font-semibold text-primary-500 dark:text-primary-400 uppercase tracking-wider mb-1">Top Picks</p>
              <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-slate-50">Most Recommended Movies</h2>
            </div>
            <Link to="/leaderboard" className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium flex items-center gap-1 text-sm transition-colors shrink-0">
              View All <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3 sm:gap-4 lg:gap-5">
            {isLoadingMovies
              ? Array.from({ length: 6 }).map((_, i) => <MediaCardSkeleton key={`movie-skel-${i}`} />)
              : movies?.map((item) => <MediaCard key={item.id} item={item} />)}
          </div>
        </div>
      </section>

      {/* Featured Celebrities */}
      <section className="py-12 sm:py-14 lg:py-16 bg-white dark:bg-slate-950 border-t border-gray-100 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-6 sm:mb-8">
            <div>
              <p className="text-xs sm:text-sm font-semibold text-primary-500 dark:text-primary-400 uppercase tracking-wider mb-1">The Curators</p>
              <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-slate-50">Featured Personalities</h2>
            </div>
            <Link to="/curators" className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium flex items-center gap-1 text-sm transition-colors shrink-0">
              View All <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-4 sm:gap-6">
            {isLoadingCelebrities
              ? Array.from({ length: 6 }).map((_, i) => <CelebrityCardSkeleton key={`celeb-skel-${i}`} />)
              : celebrities?.data.map((celebrity) => (
                  <Link key={celebrity.id} to={`/celebrities/${celebrity.slug}`} className="text-center group">
                    <div className="aspect-square rounded-full overflow-hidden bg-gray-200 dark:bg-slate-700 mb-2 sm:mb-3 mx-auto max-w-[72px] sm:max-w-[96px] lg:max-w-[112px] ring-2 ring-transparent group-hover:ring-primary-400 transition-all duration-200 shadow-sm group-hover:shadow-md">
                      {celebrity.imageUrl ? (
                        <img src={celebrity.imageUrl} alt={celebrity.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-400 dark:text-slate-500">
                          <Users className="h-8 w-8 sm:h-10 sm:w-10" aria-hidden="true" />
                        </div>
                      )}
                    </div>
                    <h3 className="font-semibold text-gray-900 dark:text-slate-100 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors text-xs sm:text-sm truncate px-1 leading-tight">
                      {celebrity.name}
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-slate-500 capitalize mt-0.5">{celebrity.category}</p>
                  </Link>
                ))}
          </div>
        </div>
      </section>

      {/* Most Recommended Shows */}
      <section className="py-12 sm:py-14 lg:py-16 bg-sky-50/50 dark:bg-slate-900 border-t border-sky-100 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-6 sm:mb-8">
            <div>
              <p className="text-xs sm:text-sm font-semibold text-primary-500 dark:text-primary-400 uppercase tracking-wider mb-1">Binge Worthy</p>
              <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-slate-50">Most Recommended Shows</h2>
            </div>
            <Link to="/leaderboard" className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium flex items-center gap-1 text-sm transition-colors shrink-0">
              View All <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3 sm:gap-4 lg:gap-5">
            {isLoadingShows
              ? Array.from({ length: 6 }).map((_, i) => <MediaCardSkeleton key={`show-skel-${i}`} />)
              : shows?.map((item) => <MediaCard key={item.id} item={item} />)}
          </div>
        </div>
      </section>

      {/* Added Recently */}
      {((recentlyAdded?.celebrities?.length ?? 0) > 0 || (recentlyAdded?.media?.length ?? 0) > 0) && (
        <section className="py-12 sm:py-14 lg:py-16 bg-white dark:bg-slate-950 border-t border-gray-100 dark:border-slate-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-6 sm:mb-8">
              <p className="text-xs sm:text-sm font-semibold text-primary-500 dark:text-primary-400 uppercase tracking-wider mb-1">Fresh Additions</p>
              <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-slate-50">Added Recently</h2>
            </div>

            {(recentlyAdded?.celebrities?.length ?? 0) > 0 && (
              <div className="mb-8">
                <h3 className="text-sm font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider mb-4">New Curators</h3>
                <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-3 sm:gap-4">
                  {recentlyAdded!.celebrities.slice(0, 8).map((celeb: any) => (
                    <Link key={celeb.id} to={`/celebrities/${celeb.slug}`} className="text-center group">
                      <div className="aspect-square rounded-full overflow-hidden bg-gray-200 dark:bg-slate-700 mb-1.5 mx-auto max-w-[56px] sm:max-w-[72px] ring-2 ring-transparent group-hover:ring-primary-400 transition-all duration-200 shadow-sm">
                        {celeb.image_url ? (
                          <img src={celeb.image_url} alt={celeb.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-400 dark:text-slate-500">
                            <Users className="h-5 w-5 sm:h-6 sm:w-6" aria-hidden="true" />
                          </div>
                        )}
                      </div>
                      <h4 className="font-medium text-gray-800 dark:text-slate-200 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors text-xs truncate px-0.5 leading-tight">
                        {celeb.name}
                      </h4>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {(recentlyAdded?.media?.length ?? 0) > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider mb-4">New Titles</h3>
                <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-3 sm:gap-4">
                  {recentlyAdded!.media.slice(0, 8).map((item: any) => (
                    <Link key={item.id} to={`/media/${item.slug}`} className="group block">
                      <div className="aspect-[2/3] rounded-lg overflow-hidden bg-gray-200 dark:bg-slate-700 mb-1.5 shadow-sm group-hover:shadow-md transition-shadow">
                        {item.poster_url ? (
                          <img src={item.poster_url} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-400 dark:text-slate-500">
                            <Film className="h-5 w-5" aria-hidden="true" />
                          </div>
                        )}
                      </div>
                      <h4 className="font-medium text-gray-800 dark:text-slate-200 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors text-xs truncate leading-tight">
                        {item.title}
                      </h4>
                      <p className="text-xs text-gray-400 dark:text-slate-500">{item.year}</p>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Recently Viewed — localStorage, only shown if user has history */}
      {recentlyViewed.length > 0 && (() => {
        const viewedCelebs = recentlyViewed.filter(i => i.type === 'celebrity')
        const viewedMedia = recentlyViewed.filter(i => i.type === 'media')
        return (
          <section className="py-10 sm:py-12 bg-gray-50 dark:bg-slate-900 border-t border-gray-100 dark:border-slate-800">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex items-center gap-2 mb-6">
                <Clock className="h-4 w-4 text-gray-400 dark:text-slate-500" aria-hidden="true" />
                <h2 className="text-base font-semibold text-gray-700 dark:text-slate-300">Recently Viewed</h2>
              </div>

              {viewedCelebs.length > 0 && (
                <div className="mb-6">
                  <p className="text-xs font-semibold text-gray-400 dark:text-slate-500 uppercase tracking-wider mb-3">Celebrities</p>
                  <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-thin">
                    {viewedCelebs.map((item) => (
                      <Link
                        key={item.id}
                        to={`/celebrities/${item.slug}`}
                        className="flex-shrink-0 group text-center w-16 sm:w-20"
                      >
                        <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full overflow-hidden bg-gray-200 dark:bg-slate-700 mb-1.5 mx-auto shadow-sm ring-2 ring-transparent group-hover:ring-primary-400 transition-all duration-200">
                          {item.imageUrl ? (
                            <img src={item.imageUrl} alt={item.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-gray-400 dark:text-slate-500">
                              <Users className="h-4 w-4" aria-hidden="true" />
                            </div>
                          )}
                        </div>
                        <p className="text-xs text-gray-700 dark:text-slate-300 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors truncate leading-tight font-medium">
                          {item.title}
                        </p>
                        <p className="text-xs text-gray-400 dark:text-slate-500 truncate capitalize">{item.subtitle}</p>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {viewedMedia.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-400 dark:text-slate-500 uppercase tracking-wider mb-3">Movies & Shows</p>
                  <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-thin">
                    {viewedMedia.map((item) => (
                      <Link
                        key={item.id}
                        to={`/media/${item.slug}`}
                        className="flex-shrink-0 group text-center w-16 sm:w-20"
                      >
                        <div className="w-full aspect-[2/3] rounded-lg overflow-hidden bg-gray-200 dark:bg-slate-700 mb-1.5 shadow-sm group-hover:shadow-md transition-shadow">
                          {item.imageUrl ? (
                            <img src={item.imageUrl} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-gray-400 dark:text-slate-500">
                              <Film className="h-4 w-4" aria-hidden="true" />
                            </div>
                          )}
                        </div>
                        <p className="text-xs text-gray-700 dark:text-slate-300 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors truncate leading-tight font-medium">
                          {item.title}
                        </p>
                        <p className="text-xs text-gray-400 dark:text-slate-500 truncate">{item.subtitle}</p>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </section>
        )
      })()}

      {/* Browse by Category */}
      <section className="py-12 sm:py-14 lg:py-16 bg-white dark:bg-slate-900 border-t border-gray-100 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-6 sm:mb-8">
            <div>
              <p className="text-xs sm:text-sm font-semibold text-primary-500 dark:text-primary-400 uppercase tracking-wider mb-1">Explore</p>
              <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-slate-50">Browse by Category</h2>
            </div>
            <Link to="/categories" className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium flex items-center gap-1 text-sm transition-colors shrink-0">
              View All <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
            {CATEGORIES.map((cat) => (
              <Link
                key={cat.value}
                to={`/category/${cat.value}`}
                className="group flex flex-col items-center gap-3 p-4 sm:p-5 rounded-xl bg-gray-50 dark:bg-slate-800 border border-gray-100 dark:border-slate-700 hover:border-primary-300 dark:hover:border-primary-600 hover:shadow-md dark:hover:shadow-slate-900/50 transition-all duration-200"
              >
                <div className={`p-3 rounded-xl ${cat.color}`} aria-hidden="true">
                  {cat.icon}
                </div>
                <span className="text-sm font-semibold text-gray-800 dark:text-slate-200 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                  {cat.label}
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA newsletter */}
      <section className="py-14 sm:py-16 lg:py-20 relative overflow-hidden bg-gradient-to-br from-primary-600 to-primary-800 dark:from-primary-950 dark:to-slate-950">
        <div className="absolute top-0 right-0 w-72 h-72 bg-white/10 dark:bg-accent-500/10 rounded-full blur-3xl pointer-events-none" aria-hidden="true" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 dark:hidden rounded-full blur-2xl pointer-events-none" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-3 sm:mb-4">
            Never Miss a Great Recommendation
          </h2>
          <p className="text-primary-100 dark:text-primary-200 mb-8 sm:mb-10 max-w-xl mx-auto text-sm sm:text-base leading-relaxed">
            Join our newsletter for weekly curated picks from the world's most interesting minds.
          </p>
          <form className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto" onSubmit={handleNewsletterSubmit} aria-label="Newsletter signup">
            <input
              type="email"
              value={newsletterEmail}
              onChange={(e) => setNewsletterEmail(sanitizeEmail(e.target.value))}
              placeholder="your@email.com"
              aria-label="Email address"
              maxLength={254}
              autoComplete="email"
              className="flex-1 px-4 py-3 rounded-xl text-gray-900 bg-white border border-transparent focus:outline-none focus:ring-2 focus:ring-white/50 text-sm shadow-sm"
            />
            <button type="submit" className="px-6 py-3 bg-white/20 hover:bg-white/30 border border-white/30 text-white text-sm font-semibold whitespace-nowrap rounded-xl transition-all duration-200">
              Subscribe
            </button>
          </form>
          {newsletterMsg && <p className="text-primary-100 dark:text-primary-200 text-xs mt-2">{newsletterMsg}</p>}
          <p className="text-primary-100 dark:text-primary-200 text-xs mt-3">
            By subscribing, you agree to our <Link to="/privacy" className="underline">Privacy Policy</Link> and <Link to="/terms" className="underline">Terms</Link>.
          </p>
        </div>
      </section>

      {/* Live Stats */}
      <section className="py-10 sm:py-12 bg-gray-50 dark:bg-slate-900 border-t border-gray-100 dark:border-slate-800" aria-label="Site statistics">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
            <div className="flex items-center justify-center gap-4 p-5 sm:p-6 rounded-xl bg-white dark:bg-primary-950/50 border border-primary-100 dark:border-primary-900/50 shadow-sm">
              <div className="p-3 bg-primary-50 dark:bg-primary-900/60 rounded-xl" aria-hidden="true">
                <Users className="h-6 w-6 text-primary-500 dark:text-primary-400" />
              </div>
              <div>
                <div className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-slate-50">{celebCount}</div>
                <div className="text-sm text-gray-500 dark:text-slate-400">Celebrities</div>
              </div>
            </div>
            <div className="flex items-center justify-center gap-4 p-5 sm:p-6 rounded-xl bg-white dark:bg-amber-950/30 border border-amber-100 dark:border-amber-900/30 shadow-sm">
              <div className="p-3 bg-amber-50 dark:bg-amber-900/40 rounded-xl" aria-hidden="true">
                <Star className="h-6 w-6 text-amber-500 dark:text-accent-400" />
              </div>
              <div>
                <div className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-slate-50">{recCount}</div>
                <div className="text-sm text-gray-500 dark:text-slate-400">Verified Picks</div>
              </div>
            </div>
            <div className="flex items-center justify-center gap-4 p-5 sm:p-6 rounded-xl bg-white dark:bg-green-950/30 border border-green-100 dark:border-green-900/30 shadow-sm">
              <div className="p-3 bg-green-50 dark:bg-green-900/40 rounded-xl" aria-hidden="true">
                <TrendingUp className="h-6 w-6 text-green-500 dark:text-green-400" />
              </div>
              <div>
                <div className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-slate-50">100%</div>
                <div className="text-sm text-gray-500 dark:text-slate-400">Source Tracked</div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
